import { onScopeDispose, reactive, ref } from 'vue';
import { submitListRequest } from '../api/listApi';
import type { Product, ProductActionStatus, UserList } from '../models/ProductModels';
import type { SearchController } from './useSearch';
import { getRecaptchaToken, openLogin, openSignup, openSubscription, trackAddToCart, trackAddToList, updateCartCount, updateWishListCount } from '../utils/legacyInterop';

function responseValue(value: unknown): string {
  if (typeof value === 'string') {
    try { return String(JSON.parse(value) as unknown); }
    catch { return value; }
  }
  return String(value ?? '');
}

export function useProductActions(search: SearchController) {
  const cartStatuses = reactive<Record<number, ProductActionStatus>>({});
  const cartStatusResetTimers: Record<number, ReturnType<typeof setTimeout>> = {};
  const selectedLists = reactive<Record<number, number>>({});
  const activeProduct = ref<Product | null>(null);
  const listName = ref('');
  const listError = ref('');
  const listModalOpen = ref(false);
  const shoppingListModalOpen = ref(false);
  const actionError = ref('');
  const isSavingList = ref(false);

  async function addCart(product: Product): Promise<void> {
    if (cartStatuses[product.ProductSK] === 'loading' || cartStatuses[product.ProductSK] === 'added') return;
    cartStatuses[product.ProductSK] = 'loading';
    actionError.value = '';
    try {
      const token = await getRecaptchaToken(search.config.recaptchaSiteKey, 'add_to_cart');
      const response = await submitListRequest<string | number>({ Value: `${product.ISBN13}|1`, Action: 'CART', Random: Math.random() }, token);
      updateCartCount(responseValue(response));
      cartStatuses[product.ProductSK] = 'added';
      cartStatusResetTimers[product.ProductSK] = setTimeout(() => {
        cartStatuses[product.ProductSK] = 'idle';
        delete cartStatusResetTimers[product.ProductSK];
      }, 7000);
      trackAddToCart(product);
    } catch (error) {
      console.error('Add to cart failed.', error);
      cartStatuses[product.ProductSK] = 'error';
      actionError.value = 'An error was encountered while adding to cart.';
    }
  }

  onScopeDispose(() => {
    Object.values(cartStatusResetTimers).forEach(clearTimeout);
  });

  async function selectList(product: Product, list: UserList): Promise<void> {
    actionError.value = '';
    try {
      const token = await getRecaptchaToken(search.config.recaptchaSiteKey, 'add_to_list');
      await submitListRequest({ Action: 'List', ListName: '', value: `${product.ProductSK}-1.,${list.ListID}` }, token);
      selectedLists[product.ProductSK] = list.ListID;
      trackAddToList(product, list.ListName);
    } catch (error) {
      console.error('Add to list failed.', error);
      actionError.value = 'An error was encountered while adding to the list.';
    }
  }

  function beginCreateList(product: Product): void {
    activeProduct.value = product;
    listName.value = '';
    listError.value = '';
    listModalOpen.value = true;
  }

  function closeListModal(): void {
    if (isSavingList.value) return;
    listModalOpen.value = false;
    activeProduct.value = null;
  }

  function beginShoppingList(product: Product): void {
    activeProduct.value = product;
    listName.value = '';
    listError.value = '';
    shoppingListModalOpen.value = true;
  }

  function closeShoppingListModal(): void {
    if (isSavingList.value) return;
    shoppingListModalOpen.value = false;
    activeProduct.value = null;
  }

  function loginFromShoppingList(): void {
    closeShoppingListModal();
    openLogin();
  }

  function signupFromShoppingList(): void {
    closeShoppingListModal();
    openSignup(search.config.signupUrl);
  }

  async function createList(destination: 'stay' | 'login' | 'signup'): Promise<void> {
    const product = activeProduct.value;
    const name = listName.value.trim();
    if (!product || isSavingList.value) return;
    if (!name) { listError.value = 'Please enter list name.'; return; }
    listError.value = '';
    isSavingList.value = true;
    try {
      const token = await getRecaptchaToken(search.config.recaptchaSiteKey, search.config.listAction || 'create_list');
      const anonymous = search.userSk.value === 0;
      const response = await submitListRequest<string | number>({
        Action: anonymous ? 'AnonList' : 'List',
        ListName: name,
        value: anonymous ? `${product.ProductSK}-1.,` : `${product.ProductSK}-1.,1`
      }, token);
      const result = responseValue(response);
      if (result === '0') { listError.value = 'List Name already exists'; return; }
      if (result.toLowerCase() === 'invalid') { listError.value = 'Please enter a valid list name'; return; }
      trackAddToList(product, name);
      if (!anonymous) {
        const listId = Number(result);
        if (Number.isFinite(listId) && listId > 0) {
          search.userLists.value.unshift({ ListID: listId, ListName: name });
          selectedLists[product.ProductSK] = listId;
          updateWishListCount(search.userLists.value.length);
        }
      }
      listModalOpen.value = false;
      if (destination === 'login') openLogin();
      if (destination === 'signup') openSignup(search.config.signupUrl);
    } catch (error) {
      console.error('Create list failed.', error);
      listError.value = 'Please enter a valid list name.';
    } finally {
      isSavingList.value = false;
    }
  }

  async function createShoppingList(destination: 'login' | 'signup'): Promise<void> {
    const product = activeProduct.value;
    const name = listName.value.trim();
    if (!product || isSavingList.value) return;
    if (!name) { listError.value = 'Please enter list name.'; return; }
    listError.value = '';
    isSavingList.value = true;
    try {
      const token = await getRecaptchaToken(search.config.recaptchaSiteKey, search.config.listAction || 'create_list');
      const response = await submitListRequest<string | number>({
        Action: 'AnonShoppingList',
        ListName: name,
        value: `${product.ProductSK}-1.,`
      }, token);
      const result = responseValue(response);
      if (result === '0') { listError.value = 'List Name already exists'; return; }
      if (result.toLowerCase() === 'invalid') { listError.value = 'Please enter a valid list name'; return; }
      trackAddToList(product, name);
      shoppingListModalOpen.value = false;
      if (destination === 'login') openLogin();
      if (destination === 'signup') openSignup(search.config.signupUrl);
    } catch (error) {
      console.error('Create shopping list failed.', error);
      listError.value = 'Please enter a valid list name.';
    } finally {
      isSavingList.value = false;
    }
  }

  function checkout(element: HTMLElement): void {
    search.isCheckoutLoading.value = true;
    openSubscription(element);
    window.setTimeout(() => { search.isCheckoutLoading.value = false; }, 1000);
  }

  return { cartStatuses, selectedLists, activeProduct, listName, listError, listModalOpen, shoppingListModalOpen, actionError, isSavingList, addCart, selectList, beginCreateList, closeListModal, beginShoppingList, closeShoppingListModal, loginFromShoppingList, signupFromShoppingList, createList, createShoppingList, checkout };
}
